import { useState } from "react";
import { Link } from "react-router-dom";
import { useMember } from "../lib/member";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useApi, useFetch, errMsg } from "../lib/api";
import { PageHeader, Empty, RiskBadge } from "../components/shared";
import { FormDialog } from "../components/FormDialog";
import { Button } from "../components/ui/button";
import { useScoreGate } from "../components/ScoreGate";

export default function Vendors() {
  const api = useApi();
  const { data, reload } = useFetch("/vendors");
  const { me } = useMember();
  const { data: employers } = useFetch("/employers");
  const [edit, setEdit] = useState(null);
  const gate = useScoreGate();

  const fields = [
    { name: "principal_id", label: "Principal employer", required: true, options: (employers || []).map((e) => ({ value: e.id, label: e.company_name })) },
    { name: "vendor_name", label: "Vendor name", required: true },
    { name: "pan_number", label: "PAN", required: true, minLength: 10, maxLength: 10 },
    { name: "epf_registration_number", label: "EPF establishment code", required: true, maxLength: 22 },
    { name: "esic_registration_number", label: "ESIC registration number", required: true, maxLength: 17 },
    { name: "clra_license_number", label: "CLRA licence number" },
    { name: "clra_max_workers", label: "CLRA max workers", type: "number", required: true },
    { name: "clra_license_expiry", label: "CLRA licence expiry (for reminder emails)", type: "date" },
    { name: "contact_email", label: "Contractor email (receives salary slips)", type: "email" },
  ];

  const save = async (form) => {
    const { company_name, worker_count, risk_status, id, created_at, notified, ...rest } = form;
    const body = { ...rest, clra_max_workers: Number(form.clra_max_workers) || 20, clra_license_expiry: form.clra_license_expiry || null, contact_email: form.contact_email || null };
    const ok = await gate.run((r) => (edit.id ? api.put(`/vendors/${edit.id}`, { ...body, score_override_reason: r }) : api.post("/vendors", body)));
    if (ok) { toast.success("Vendor saved"); reload(); }
    return ok;
  };
  const remove = async (v) => {
    if (!window.confirm(`Delete ${v.vendor_name} and all its workers and records?`)) return;
    try { await api.delete(`/vendors/${v.id}`); toast.success("Deleted"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Contractors" title="Vendor partners" desc="Third-party contractors supplying workers, with statutory registrations and risk status.">
        <Button onClick={() => (employers?.length ? setEdit({ clra_max_workers: 20 }) : toast.error("Add a principal employer first"))}
          data-testid="vendor-add-button" className="bg-[#00E599] text-[#0B1017] hover:bg-[#00C482] font-semibold">
          <Plus size={15} className="mr-1.5" />Add vendor
        </Button>
      </PageHeader>
      <div className="c-surface overflow-x-auto c-rise">
        <table className="c-table w-full">
          <thead><tr><th>Vendor</th><th>Employer</th><th>PAN</th><th>EPF code</th><th>ESIC no.</th><th>CLRA</th><th>Workers</th><th>Risk</th><th /></tr></thead>
          <tbody>
            {(data || []).map((v) => (
              <tr key={v.id} data-testid="vendor-table-row">
                <td className="font-medium">{me?.role === "vendor" ? v.vendor_name : <Link to={`/contractors/${v.id}`} className="hover:text-[#00E599] underline-offset-4 hover:underline" data-testid="vendor-profile-link">{v.vendor_name}</Link>}</td>
                <td className="text-slate-400">{v.company_name}</td>
                <td className="font-mono text-xs">{v.pan_number}</td>
                <td className="font-mono text-xs">{v.epf_registration_number}</td>
                <td className="font-mono text-xs">{v.esic_registration_number}</td>
                <td className="font-mono text-xs text-slate-400">{v.clra_license_number || "—"}</td>
                <td className={`font-mono ${v.worker_count > v.clra_max_workers ? "text-rose-400" : ""}`}>{v.worker_count}/{v.clra_max_workers}</td>
                <td><RiskBadge status={v.risk_status} /></td>
                <td className="text-right">
                  <button className="p-1.5 text-slate-400 hover:text-white" onClick={() => setEdit(v)} data-testid="vendor-edit-btn"><Pencil size={14} /></button>
                  <button className="p-1.5 text-slate-400 hover:text-rose-400" onClick={() => remove(v)} data-testid="vendor-delete-btn"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data?.length === 0 && <Empty text="No vendor partners yet." />}
      </div>
      {edit && <FormDialog open onOpenChange={() => setEdit(null)} title={edit.id ? "Edit vendor" : "Add vendor partner"}
        fields={fields} initial={edit} onSubmit={save} testid="vendor-form" />}
      {gate.dialog}
    </div>
  );
}
